import { createSlice, Slice } from '@reduxjs/toolkit';
import { initPaginatedListData } from 'modules/shared/constants';
import { IPaginatedData } from 'modules/shared/interfaces/paginationMeta';
import { IReqStatus } from 'modules/shared/interfaces/requestStatus';
import moment from 'moment';
import { RootStateOrAny } from 'react-redux';
import {
	IEndUser,
	IEndUserBase,
	IEndUserCompletedLesson,
	IEndUserDetails,
	IEndUserGrammarWordReview,
	IEndUserSubjectReview,
	IEndUserVerbWordReview,
	IEndUserVocabWordReview,
} from '../interfaces/EndUser';

export const END_USERS_MANAGEMENT_KEY_IN_STORE = 'endUsersManagement';

interface EndUsersManagementState {
	isLoading: boolean;
	searchByEmailAutocomplete: {
		isLoading: boolean;
		result: IEndUserBase[];
	};
	searchByNameAutocomplete: {
		isLoading: boolean;
		result: IEndUserBase[];
	};
	endUsers: IPaginatedData<IEndUser>;
	actions: {
		isLoading: boolean;
		status: IReqStatus;
	};

	endUser?: IEndUserDetails;

	endUserCompletedLessons: {
		isLoading: boolean;
		data: IPaginatedData<IEndUserCompletedLesson>;
	};

	vocabLessonWordsReview: {
		isLoading: boolean;
		data: IEndUserVocabWordReview[];
	};

	sightLessonWordsReview: {
		isLoading: boolean;
		data: IEndUserVocabWordReview[];
	};

	verbLessonWordsReview: {
		isLoading: boolean;
		data: IEndUserVerbWordReview[];
	};
	grammarLessonWordsReview: {
		isLoading: boolean;
		data: IEndUserGrammarWordReview[];
	};
	sortingLessonWordsReview: {
		isLoading: boolean;
		data: IEndUserSubjectReview[];
	};
}

const initialState: EndUsersManagementState = {
	isLoading: false,
	searchByEmailAutocomplete: {
		isLoading: false,
		result: [],
	},
	searchByNameAutocomplete: {
		isLoading: false,
		result: [],
	},
	endUsers: initPaginatedListData,
	actions: {
		isLoading: false,
		status: {
			error: false,
			success: false,
		},
	},
	endUser: undefined,
	endUserCompletedLessons: {
		isLoading: false,
		data: initPaginatedListData,
	},

	vocabLessonWordsReview: {
		isLoading: false,
		data: [],
	},

	sightLessonWordsReview: {
		isLoading: false,
		data: [],
	},

	verbLessonWordsReview: {
		isLoading: false,
		data: [],
	},

	grammarLessonWordsReview: {
		isLoading: false,
		data: [],
	},

	sortingLessonWordsReview: {
		isLoading: false,
		data: [],
	},
};

const endUsersManagementSlice: Slice = createSlice({
	name: END_USERS_MANAGEMENT_KEY_IN_STORE,
	initialState: initialState,
	reducers: {
		resetEndUsersManagementData: () => {
			return initialState;
		},
		getEndUsersManagementData: (state: EndUsersManagementState) => {
			state.isLoading = true;
		},
		getEndUsers: (state: EndUsersManagementState) => {
			state.isLoading = true;
		},
		getEndUsersSuccess: (state: EndUsersManagementState, action) => {
			state.isLoading = false;
			state.endUsers = action.payload;
		},
		getEndUsersFailure: (state: EndUsersManagementState) => {
			state.isLoading = false;
			state.endUsers = initialState.endUsers;
		},

		searchEndUsersByName: (state: EndUsersManagementState) => {
			state.searchByNameAutocomplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchEndUsersByNameResult: (state: EndUsersManagementState, action) => {
			state.searchByNameAutocomplete = {
				isLoading: false,
				result: action.payload,
			};
		},
		resetSearchEndUsersByName: (state: EndUsersManagementState) => {
			state.searchByNameAutocomplete = {
				isLoading: false,
				result: [],
			};
		},

		searchEndUsersByEmail: (state: EndUsersManagementState) => {
			state.searchByEmailAutocomplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchEndUsersByEmailResult: (
			state: EndUsersManagementState,
			action,
		) => {
			state.searchByEmailAutocomplete = {
				isLoading: false,
				result: action.payload,
			};
		},
		resetSearchEndUsersByEmail: (state: EndUsersManagementState) => {
			state.searchByEmailAutocomplete = {
				isLoading: false,
				result: [],
			};
		},

		handleLockEndUserAction: (state: EndUsersManagementState, action) => {
			state.actions.isLoading = true;
		},
		handleUnlockEndUserAction: (state: EndUsersManagementState, action) => {
			state.actions.isLoading = true;
		},
		handleResetPassEndUserAction: (state: EndUsersManagementState, action) => {
			state.actions.isLoading = true;
		},

		handleLockEndUserActionSuccess(state: EndUsersManagementState, action) {
			state.endUsers.items = state.endUsers.items.map(user => {
				if (user.id === action.payload.endUserId) {
					user.lockedAt = moment().format();
				}

				return user;
			});
			state.actions = {
				isLoading: false,
				status: {
					error: false,
					success: true,
				},
			};
		},

		handleUnLockEndUserActionSuccess(state: EndUsersManagementState, action) {
			state.endUsers.items = state.endUsers.items.map(user => {
				if (user.id === action.payload.endUserId) {
					user.lockedAt = undefined;
				}

				return user;
			});
			state.actions = {
				isLoading: false,
				status: {
					error: false,
					success: true,
				},
			};
		},

		handleEndUserActionSuccess: (state: EndUsersManagementState) => {
			state.actions = {
				isLoading: false,
				status: {
					error: false,
					success: true,
				},
			};
		},
		handleEndUserActionFailure: (state: EndUsersManagementState) => {
			state.actions = {
				isLoading: false,
				status: {
					error: true,
					success: false,
				},
			};
		},

		getEndUserDetails: (state: EndUsersManagementState) => {
			state.isLoading = true;
		},

		handleGetEndUserDetailsSuccess: (
			state: EndUsersManagementState,
			action,
		) => {
			state.isLoading = false;
			state.endUser = action.payload;
		},

		handleGetEndUserDetailsFailure: (state: EndUsersManagementState) => {
			state.isLoading = false;
			state.endUser = undefined;
		},

		getEndUserCompletedLessons: (state: EndUsersManagementState) => {
			state.endUserCompletedLessons.isLoading = true;
		},
		handleGetEndUserCompletedLessonsSuccess: (
			state: EndUsersManagementState,
			action,
		) => {
			state.endUserCompletedLessons.isLoading = false;
			state.endUserCompletedLessons.data = action.payload;
		},
		handleGetEndUserCompletedLessonsFailure: (
			state: EndUsersManagementState,
		) => {
			state.endUserCompletedLessons = initialState.endUserCompletedLessons;
		},

		getVocabLessonWordsReview: (state: EndUsersManagementState) => {
			state.vocabLessonWordsReview = {
				isLoading: true,
				data: [],
			};
		},
		handleGetVocabLessonWordsReviewSuccess: (
			state: EndUsersManagementState,
			action,
		) => {
			state.vocabLessonWordsReview = {
				isLoading: false,
				data: action.payload,
			};
		},

		handleGetVocabLessonWordsReviewFailure: (
			state: EndUsersManagementState,
		) => {
			state.vocabLessonWordsReview = {
				isLoading: false,
				data: [],
			};
		},

		getSightLessonWordsReview: (state: EndUsersManagementState) => {
			state.sightLessonWordsReview = {
				isLoading: true,
				data: [],
			};
		},
		handleGetSightLessonWordsReviewSuccess: (
			state: EndUsersManagementState,
			action,
		) => {
			state.sightLessonWordsReview = {
				isLoading: false,
				data: action.payload,
			};
		},
		handleGetSightLessonWordsReviewFailure: (
			state: EndUsersManagementState,
		) => {
			state.sightLessonWordsReview = {
				isLoading: false,
				data: [],
			};
		},

		getVerbLessonWordsReview: (state: EndUsersManagementState) => {
			state.verbLessonWordsReview = {
				isLoading: true,
				data: [],
			};
		},
		handleGetVerbLessonWordsReviewSuccess: (
			state: EndUsersManagementState,
			action,
		) => {
			state.verbLessonWordsReview = {
				isLoading: false,
				data: action.payload,
			};
		},

		handleGetVerbLessonWordsReviewFailure: (state: EndUsersManagementState) => {
			state.verbLessonWordsReview = {
				isLoading: false,
				data: [],
			};
		},

		getGrammarLessonWordsReview: (state: EndUsersManagementState) => {
			state.grammarLessonWordsReview = {
				isLoading: true,
				data: [],
			};
		},
		handleGetGrammarLessonWordsReviewSuccess: (
			state: EndUsersManagementState,
			action,
		) => {
			state.grammarLessonWordsReview = {
				isLoading: false,
				data: action.payload,
			};
		},

		handleGetGrammarLessonWordsReviewFailure: (
			state: EndUsersManagementState,
		) => {
			state.grammarLessonWordsReview = {
				isLoading: false,
				data: [],
			};
		},

		getSortingLessonWordsReview: (state: EndUsersManagementState) => {
			state.sortingLessonWordsReview = {
				isLoading: true,
				data: [],
			};
		},
		handleGetSortingLessonWordsReviewSuccess: (
			state: EndUsersManagementState,
			action,
		) => {
			state.sortingLessonWordsReview = {
				isLoading: false,
				data: action.payload,
			};
		},

		handleGetSortingLessonWordsReviewFailure: (
			state: EndUsersManagementState,
		) => {
			state.sortingLessonWordsReview = {
				isLoading: false,
				data: [],
			};
		},
	},
});

export const {
	resetEndUsersManagementData,
	getEndUsersManagementData,
	getEndUsers,
	getEndUsersSuccess,
	getEndUsersFailure,
	searchEndUsersByName,
	setSearchEndUsersByNameResult,
	resetSearchEndUsersByName,
	searchEndUsersByEmail,
	setSearchEndUsersByEmailResult,
	resetSearchEndUsersByEmail,
	handleLockEndUserAction,
	handleUnlockEndUserAction,
	handleResetPassEndUserAction,
	handleLockEndUserActionSuccess,
	handleUnLockEndUserActionSuccess,
	handleEndUserActionFailure,
	handleEndUserActionSuccess,
	getEndUserDetails,
	handleGetEndUserDetailsSuccess,
	handleGetEndUserDetailsFailure,

	getEndUserCompletedLessons,
	handleGetEndUserCompletedLessonsSuccess,
	handleGetEndUserCompletedLessonsFailure,

	getVocabLessonWordsReview,
	handleGetVocabLessonWordsReviewSuccess,
	handleGetVocabLessonWordsReviewFailure,

	getVerbLessonWordsReview,
	handleGetVerbLessonWordsReviewSuccess,
	handleGetVerbLessonWordsReviewFailure,

	getGrammarLessonWordsReview,
	handleGetGrammarLessonWordsReviewSuccess,
	handleGetGrammarLessonWordsReviewFailure,

	getSortingLessonWordsReview,
	handleGetSortingLessonWordsReviewSuccess,
	handleGetSortingLessonWordsReviewFailure,

	getSightLessonWordsReview,
	handleGetSightLessonWordsReviewSuccess,
	handleGetSightLessonWordsReviewFailure,
} = endUsersManagementSlice.actions;

export const EndUsersManagementStateSelector = (
	state: RootStateOrAny,
): EndUsersManagementState => state[END_USERS_MANAGEMENT_KEY_IN_STORE];

export const endUsersManagementSliceReducer = endUsersManagementSlice.reducer;

export default endUsersManagementSlice;
